<template>
  <div class="be-page">
    <div class="be-header">
      <button class="be-back" @click="$router.push({ name: 'admin-blog' })">
        <feather-icon icon="ArrowLeftIcon" size="15" class="mr-50" />Blog
      </button>
      <div class="be-header__actions">
        <button class="be-btn be-btn--ghost" @click="save('draft')" :disabled="saving">Salvar Rascunho</button>
        <button class="be-btn be-btn--primary" @click="save('published')" :disabled="saving">
          {{ saving ? 'Salvando...' : 'Publicar' }}
        </button>
      </div>
    </div>

    <div class="be-body">
      <!-- Main -->
      <div class="be-main">
        <div class="be-tabs">
          <button class="be-tab" :class="{ 'be-tab--active': tab === 'editor' }" @click="tab = 'editor'">Editor</button>
          <button class="be-tab" :class="{ 'be-tab--active': tab === 'seo' }" @click="tab = 'seo'">SEO</button>
          <button class="be-tab" :class="{ 'be-tab--active': tab === 'ia' }" @click="tab = 'ia'">IA</button>
        </div>

        <!-- Tab: Editor -->
        <div v-show="tab === 'editor'" class="be-tab-content">
          <div class="be-field">
            <label class="be-label">Titulo</label>
            <input v-model="form.title" class="be-input be-input--lg" placeholder="Titulo do artigo" @blur="autoSlug" />
          </div>
          <div class="be-field">
            <label class="be-label">Slug <span class="be-hint">/blog/{{ form.slug || '...' }}</span></label>
            <input v-model="form.slug" class="be-input" placeholder="slug-do-artigo" />
          </div>
          <div class="be-field">
            <label class="be-label">Resumo</label>
            <textarea v-model="form.excerpt" class="be-textarea" rows="3" placeholder="Resumo curto (ate 160 caracteres)"></textarea>
            <span class="be-counter" :class="{ 'be-counter--warn': (form.excerpt || '').length > 160 }">{{ (form.excerpt || '').length }}/160</span>
          </div>
          <div class="be-field">
            <label class="be-label">Conteudo</label>
            <div class="be-editor-wrap">
              <div class="be-editor-toolbar">
                <button @click="execCmd('bold')" title="Negrito"><strong>B</strong></button>
                <button @click="execCmd('italic')" title="Italico"><em>I</em></button>
                <button @click="execCmd('insertUnorderedList')" title="Lista">&#8226;</button>
                <button @click="insertHeading('h2')" title="H2">H2</button>
                <button @click="insertHeading('h3')" title="H3">H3</button>
                <button @click="insertLink()" title="Link">&#128279;</button>
              </div>
              <div
                ref="contentEditor"
                class="be-editor"
                contenteditable="true"
                @input="onEditorInput"
                v-html="form.content"
              ></div>
            </div>
          </div>
        </div>

        <!-- Tab: SEO -->
        <div v-show="tab === 'seo'" class="be-tab-content">
          <div class="be-field">
            <label class="be-label">Meta Title <span class="be-counter">{{ (form.meta_title || '').length }}/60</span></label>
            <input v-model="form.meta_title" class="be-input" placeholder="Titulo para mecanismos de busca" maxlength="60" />
          </div>
          <div class="be-field">
            <label class="be-label">Meta Description <span class="be-counter">{{ (form.meta_description || '').length }}/155</span></label>
            <textarea v-model="form.meta_description" class="be-textarea" rows="3" placeholder="Descricao para mecanismos de busca" maxlength="155"></textarea>
          </div>
          <div class="be-field">
            <label class="be-label">Keywords</label>
            <input v-model="form.keywords" class="be-input" placeholder="palavra1, palavra2, palavra3" />
          </div>
          <button class="be-btn be-btn--ai" @click="generateSEO" :disabled="generatingSEO">
            {{ generatingSEO ? 'Gerando...' : 'Gerar SEO com IA' }}
          </button>
        </div>

        <!-- Tab: IA -->
        <div v-show="tab === 'ia'" class="be-tab-content">
          <div class="be-ia-card">
            <h3>Gerar Artigo com IA</h3>
            <p>Descreva o tema e a IA vai escrever um artigo completo otimizado para SEO.</p>
            <div class="be-field">
              <input v-model="aiTopic" class="be-input" placeholder="Ex: Como aumentar a conversao da sua VSL em 2026" />
            </div>
            <div class="be-field">
              <input v-model="aiKeywords" class="be-input" placeholder="Keywords (opcional): vsl, conversao, video marketing" />
            </div>
            <button class="be-btn be-btn--ai be-btn--full" @click="generateArticle" :disabled="generatingArticle">
              {{ generatingArticle ? 'Gerando artigo...' : 'Gerar Artigo Completo' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <aside class="be-sidebar">
        <div class="be-sidebar-card">
          <label class="be-label">Categoria</label>
          <select v-model="form.category_id" class="be-select">
            <option :value="null">Sem categoria</option>
            <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
          </select>
        </div>
        <div class="be-sidebar-card">
          <label class="be-label">Imagem de Capa</label>
          <div v-if="form.cover_image" class="be-cover-preview">
            <img :src="form.cover_image" alt="Capa" />
            <button class="be-cover-remove" @click="form.cover_image = ''">&times;</button>
          </div>
          <button class="be-btn be-btn--ai be-btn--full be-btn--img" @click="generateImage" :disabled="generatingImage" style="margin-bottom:10px">
            <feather-icon v-if="!generatingImage" icon="ImageIcon" size="14" class="mr-50" />
            {{ generatingImage ? generatingImageText : 'Gerar Imagem com IA' }}
          </button>
          <div v-if="generatingImage" class="be-img-progress">
            <div class="be-img-progress__bar"><div class="be-img-progress__fill"></div></div>
            <span class="be-img-progress__hint">Gerando imagem hiperrealista...</span>
          </div>
          <input v-model="form.cover_image" class="be-input" placeholder="URL da imagem (ou gere com IA)" />
        </div>
        <div class="be-sidebar-card">
          <label class="be-label">Status</label>
          <div class="be-status-badge" :class="'be-status--' + form.status">{{ form.status === 'published' ? 'Publicado' : form.status === 'archived' ? 'Arquivado' : 'Rascunho' }}</div>
        </div>

        <!-- SEO Analysis -->
        <div class="be-sidebar-card be-seo-card">
          <div class="be-seo-header">
            <label class="be-label" style="margin:0">Analise SEO</label>
            <span class="be-seo-score" :class="seoScoreClass">{{ seoScore }}%</span>
          </div>
          <div class="be-seo-bar"><div class="be-seo-bar__fill" :style="{ width: seoScore + '%' }" :class="seoScoreClass"></div></div>
          <span class="be-seo-level" :class="seoScoreClass">{{ seoScore >= 80 ? 'Excelente' : seoScore >= 60 ? 'Bom' : seoScore >= 40 ? 'Regular' : 'Fraco' }}</span>

          <div class="be-seo-items">
            <div class="be-seo-item" v-for="item in seoChecks" :key="item.label">
              <span class="be-seo-icon" :class="item.ok ? 'be-seo-icon--ok' : 'be-seo-icon--fail'">
                <feather-icon :icon="item.ok ? 'CheckCircleIcon' : 'XCircleIcon'" size="16" />
              </span>
              <div>
                <div class="be-seo-item__label">{{ item.label }}</div>
                <div class="be-seo-item__hint">{{ item.hint }}</div>
              </div>
            </div>
          </div>

          <button class="be-btn be-btn--ai be-btn--full" @click="generateSEO" :disabled="generatingSEO" style="margin-top:12px">
            <feather-icon v-if="!generatingSEO" icon="ZapIcon" size="14" class="mr-50" />
            {{ generatingSEO ? 'Gerando...' : 'Gerar com IA' }}
          </button>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BlogEditor',
  data() {
    return {
      tab: 'editor',
      saving: false,
      generatingArticle: false,
      generatingSEO: false,
      generatingImage: false,
      generatingImageText: 'Gerando imagem...',
      aiTopic: '',
      aiKeywords: '',
      categories: [],
      isEdit: false,
      form: {
        title: '',
        slug: '',
        content: '',
        excerpt: '',
        cover_image: '',
        category_id: null,
        status: 'draft',
        meta_title: '',
        meta_description: '',
        keywords: '',
        og_image: '',
        ai_generated: false,
      },
    }
  },
  computed: {
    contentText() {
      return (this.form.content || '').replace(/<[^>]*>/g, '')
    },
    wordCount() {
      const t = this.contentText.trim()
      return t ? t.split(/\s+/).length : 0
    },
    internalLinks() {
      const matches = (this.form.content || '').match(/href=["'][^"']*(?:vslbooster|localhost|\/blog|\/cadastro)[^"']*/gi)
      return matches ? matches.length : 0
    },
    externalLinks() {
      const all = (this.form.content || '').match(/href=["']https?:\/\//gi)
      return (all ? all.length : 0) - this.internalLinks
    },
    seoChecks() {
      const titleLen = (this.form.meta_title || '').length
      const descLen = (this.form.meta_description || '').length
      const kwCount = (this.form.keywords || '').split(',').filter(k => k.trim()).length
      const excerptLen = (this.form.excerpt || '').length
      return [
        { label: 'Titulo SEO', hint: `${titleLen}/60 caracteres (ideal: 30-60)`, ok: titleLen >= 30 && titleLen <= 60 },
        { label: 'Meta Descricao', hint: `${descLen}/155 caracteres (ideal: 120-155)`, ok: descLen >= 120 && descLen <= 155 },
        { label: 'Palavras-chave', hint: `${kwCount} palavras-chave (minimo: 3)`, ok: kwCount >= 3 },
        { label: 'Resumo/Excerpt', hint: `${excerptLen} caracteres (ideal: 50-200)`, ok: excerptLen >= 50 && excerptLen <= 200 },
        { label: 'Imagem Destacada', hint: this.form.cover_image ? 'Imagem definida \u2713' : 'Sem imagem de capa', ok: !!this.form.cover_image },
        { label: 'Tamanho do Conteudo', hint: `${this.wordCount} palavras (minimo: 300)`, ok: this.wordCount >= 300 },
        { label: 'Links Internos', hint: `${this.internalLinks} links internos (recomendado: 2+)`, ok: this.internalLinks >= 2 },
        { label: 'Links Externos', hint: `${this.externalLinks} links externos (recomendado: 1+)`, ok: this.externalLinks >= 1 },
      ]
    },
    seoScore() {
      const checks = this.seoChecks
      const passed = checks.filter(c => c.ok).length
      return Math.round((passed / checks.length) * 100)
    },
    seoScoreClass() {
      if (this.seoScore >= 80) return 'be-seo--green'
      if (this.seoScore >= 60) return 'be-seo--blue'
      if (this.seoScore >= 40) return 'be-seo--yellow'
      return 'be-seo--red'
    },
  },
  async mounted() {
    await this.fetchCategories()
    const id = this.$route.params.id
    if (id) {
      this.isEdit = true
      await this.fetchPost(id)
    }
  },
  methods: {
    api(method, url, body, extraOpts = {}) {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token')
      const opts = {
        baseURL: '',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        ...extraOpts,
      }
      if (method === 'get' || method === 'delete') return this.$http[method](url, opts)
      return this.$http[method](url, body, opts)
    },
    async fetchCategories() {
      try {
        const { data } = await this.api('get', '/api/dkbackend/blog/categories')
        this.categories = data
      } catch {}
    },
    async fetchPost(id) {
      try {
        const { data } = await this.api('get', `/api/dkbackend/blog/admin/posts/${id}`)
        Object.keys(this.form).forEach(k => { if (data[k] !== undefined) this.form[k] = data[k] })
        this.$nextTick(() => { if (this.$refs.contentEditor) this.$refs.contentEditor.innerHTML = this.form.content || '' })
      } catch (e) {
        console.error(e)
        const status = e && e.response && e.response.status
        this.$message({
          type: 'error',
          message: status === 401
            ? 'Sessão expirada. Faça login novamente para editar o artigo.'
            : 'Não foi possível carregar o artigo para edição.',
        })
      }
    },
    autoSlug() {
      if (!this.form.slug && this.form.title) {
        this.form.slug = this.form.title
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 200)
      }
    },
    onEditorInput() {
      this.form.content = this.$refs.contentEditor.innerHTML
    },
    execCmd(cmd) { document.execCommand(cmd, false, null) },
    insertHeading(tag) {
      document.execCommand('formatBlock', false, tag)
    },
    insertLink() {
      const url = prompt('URL do link:')
      if (url) document.execCommand('createLink', false, url)
    },
    async save(status) {
      this.saving = true
      this.form.status = status
      this.form.content = this.$refs.contentEditor ? this.$refs.contentEditor.innerHTML : this.form.content
      try {
        const url = this.isEdit
          ? `/api/dkbackend/blog/admin/posts/${this.$route.params.id}`
          : '/api/dkbackend/blog/admin/posts'
        const method = this.isEdit ? 'put' : 'post'
        const { data } = await this.api(method, url, this.form)
        if (!this.isEdit && data.id) {
          this.isEdit = true
          this.$router.replace({ name: 'admin-blog-editor', params: { id: data.id } })
        }
        this.$message({ type: 'success', message: status === 'published' ? 'Artigo publicado!' : 'Rascunho salvo!' })
      } catch (e) {
        this.$message({ type: 'error', message: 'Erro ao salvar' })
      }
      this.saving = false
    },
    async generateArticle() {
      if (!this.aiTopic) return
      this.generatingArticle = true
      try {
        const { data } = await this.api('post', '/api/dkbackend/blog/admin/generate', { topic: this.aiTopic, keywords: this.aiKeywords }, { timeout: 120000 })
        if (data.title) this.form.title = data.title
        if (data.content) { this.form.content = data.content; this.$nextTick(() => { if (this.$refs.contentEditor) this.$refs.contentEditor.innerHTML = data.content }) }
        if (data.excerpt) this.form.excerpt = data.excerpt
        if (data.meta_title) this.form.meta_title = data.meta_title
        if (data.meta_description) this.form.meta_description = data.meta_description
        if (data.keywords) this.form.keywords = data.keywords
        this.form.ai_generated = true
        this.autoSlug()
        this.tab = 'editor'
        this.$message({ type: 'success', message: 'Artigo gerado com sucesso! Gerando imagem de capa...' })
        // Auto-gerar imagem de capa
        this.generateImage()
      } catch (e) {
        this.$message({ type: 'error', message: 'Erro ao gerar artigo' })
      }
      this.generatingArticle = false
    },
    async generateImage() {
      const topic = this.form.title || this.aiTopic
      if (!topic) {
        this.$message({ type: 'warning', message: 'Preencha o titulo ou o tema na aba IA primeiro' })
        return
      }
      this.generatingImage = true
      this.generatingImageText = 'Gerando imagem hiperrealista...'
      try {
        const { data } = await this.api('post', '/api/dkbackend/blog/admin/generate-image', { topic, title: this.form.title }, { timeout: 120000 })
        if (data.error) {
          this.$message({ type: 'error', message: data.error })
        } else if (data.url) {
          this.form.cover_image = data.url
          this.form.og_image = data.url
          this.$message({
            type: 'success',
            message: data.temporary ? 'Imagem gerada! (URL temporaria - salve o artigo logo)' : 'Imagem hiperrealista gerada e salva!'
          })
        }
      } catch (e) {
        const msg = e.response?.data?.error || e.response?.data?.detail || e.message || 'Erro ao gerar imagem'
        console.error('[generateImage]', msg, e)
        this.$message({ type: 'error', message: msg })
      }
      this.generatingImage = false
    },
    async generateSEO() {
      this.generatingSEO = true
      try {
        const { data } = await this.api('post', '/api/dkbackend/blog/admin/generate-seo', { title: this.form.title, content: this.form.content })
        if (data.meta_title) this.form.meta_title = data.meta_title
        if (data.meta_description) this.form.meta_description = data.meta_description
        if (data.keywords) this.form.keywords = data.keywords
        this.$message({ type: 'success', message: 'SEO gerado!' })
      } catch {
        this.$message({ type: 'error', message: 'Erro ao gerar SEO' })
      }
      this.generatingSEO = false
    },
  },
}
</script>

<style lang="scss" scoped>
$orange: #E86A10;
.be-page { padding: 0 0 60px; color: #e2e8f0; }
.be-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
.be-header__actions { display: flex; gap: 8px; }
.be-back { display: flex; align-items: center; background: none; border: none; font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.65); cursor: pointer; font-family: inherit; &:hover { color: $orange; } }

.be-btn {
  padding: 10px 20px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; border: none; font-family: inherit; transition: all 0.18s;
  &--ghost { background: rgba(255,255,255,0.06); border: 1.5px solid rgba(255,255,255,0.12); color: #cbd5e1; &:hover { border-color: $orange; color: $orange; } }
  &--primary { background: $orange; color: #fff; &:hover { opacity: 0.9; } }
  &--ai { background: linear-gradient(135deg, #8B5CF6, #6366F1); color: #fff; margin-top: 8px; &:hover { opacity: 0.9; } }
  &--full { width: 100%; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.be-body { display: grid; grid-template-columns: 1fr 320px; gap: 24px; }
@media (max-width: 900px) { .be-body { grid-template-columns: 1fr; } }

.be-main { min-width: 0; }
.be-tabs { display: flex; gap: 4px; margin-bottom: 20px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; padding: 4px; width: fit-content; }
.be-tab { padding: 8px 18px; border: none; background: none; border-radius: 8px; font-size: 13px; font-weight: 600; color: rgba(255,255,255,0.55); cursor: pointer; font-family: inherit; &--active { background: rgba(255,255,255,0.1); color: $orange; box-shadow: 0 1px 4px rgba(0,0,0,0.25); } }

.be-field { margin-bottom: 16px; }
.be-label { display: block; font-size: 12px; font-weight: 700; color: rgba(255,255,255,0.75); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.3px; }
.be-hint { font-weight: 500; color: rgba(255,255,255,0.45); text-transform: none; letter-spacing: 0; }
.be-input { width: 100%; padding: 10px 14px; border: 1.5px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.06); border-radius: 8px; font-size: 14px; color: #e2e8f0; font-family: inherit; outline: none; &::placeholder { color: rgba(255,255,255,0.35); } &:focus { border-color: $orange; background: rgba(255,255,255,0.08); } &--lg { font-size: 28px; font-weight: 800; padding: 14px; letter-spacing: -0.02em; } }
.be-textarea { width: 100%; padding: 10px 14px; border: 1.5px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.06); border-radius: 8px; font-size: 14px; color: #e2e8f0; font-family: inherit; outline: none; resize: vertical; &::placeholder { color: rgba(255,255,255,0.35); } &:focus { border-color: $orange; background: rgba(255,255,255,0.08); } }
.be-select { width: 100%; padding: 10px 14px; border: 1.5px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.06); color: #e2e8f0; border-radius: 8px; font-size: 14px; font-family: inherit; outline: none; &:focus { border-color: $orange; background: rgba(255,255,255,0.08); } }
.be-select option { background: #1a2030; color: #e2e8f0; }
.be-counter { font-size: 11px; color: rgba(255,255,255,0.45); &--warn { color: #EF4444; } }

.be-editor-wrap { border: 1.5px solid rgba(255,255,255,0.12); border-radius: 10px; overflow: hidden; background: rgba(255,255,255,0.04); }
.be-editor-toolbar { display: flex; gap: 4px; padding: 8px 12px; background: rgba(255,255,255,0.06); border-bottom: 1px solid rgba(255,255,255,0.12); }
.be-editor-toolbar button { width: 32px; height: 32px; border: none; background: none; border-radius: 6px; cursor: pointer; font-size: 14px; color: rgba(255,255,255,0.75); &:hover { background: rgba(255,255,255,0.12); color: #fff; } }
.be-editor { min-height: 400px; padding: 20px; font-size: 15px; line-height: 1.7; color: #e2e8f0; outline: none; }

.be-sidebar-card { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 16px; margin-bottom: 16px; }
.be-cover-preview { position: relative; margin-bottom: 10px; img { width: 100%; border-radius: 8px; } }
.be-cover-remove { position: absolute; top: 6px; right: 6px; width: 24px; height: 24px; border-radius: 50%; background: rgba(0,0,0,0.6); color: #fff; border: none; cursor: pointer; font-size: 16px; line-height: 1; }

.be-status-badge { display: inline-block; padding: 4px 14px; border-radius: 6px; font-size: 12px; font-weight: 700; }
.be-status--draft { background: rgba(245,185,53,0.16); color: #F5B935; }
.be-status--published { background: rgba(16,185,129,0.16); color: #10B981; }
.be-status--archived { background: rgba(255,255,255,0.12); color: rgba(255,255,255,0.6); }

.be-ia-card { background: linear-gradient(135deg, rgba(139,92,246,0.12), rgba(99,102,241,0.08)); border: 1px solid rgba(139,92,246,0.35); border-radius: 14px; padding: 24px; }
.be-ia-card h3 { font-size: 1.1rem; font-weight: 800; color: #c4b5fd; margin: 0 0 8px; }
.be-ia-card p { font-size: 0.88rem; color: rgba(196,181,253,0.8); margin: 0 0 16px; }

.be-btn--img { background: linear-gradient(135deg, $orange, #F59E0B); }

// SEO Analysis
.be-seo-card { padding: 20px; }
.be-seo-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.be-seo-score { font-size: 22px; font-weight: 900; }
.be-seo-bar { height: 8px; background: rgba(255,255,255,0.12); border-radius: 6px; overflow: hidden; margin-bottom: 4px; }
.be-seo-bar__fill { height: 100%; border-radius: 6px; transition: width 0.4s ease; }
.be-seo-level { font-size: 12px; font-weight: 700; display: block; text-align: right; margin-bottom: 14px; }

.be-seo--green { color: #059669; }
.be-seo--green.be-seo-bar__fill { background: linear-gradient(90deg, #10B981, #059669); }
.be-seo--blue { color: #2563EB; }
.be-seo--blue.be-seo-bar__fill { background: linear-gradient(90deg, #3B82F6, #2563EB); }
.be-seo--yellow { color: #D97706; }
.be-seo--yellow.be-seo-bar__fill { background: linear-gradient(90deg, #F59E0B, #D97706); }
.be-seo--red { color: #DC2626; }
.be-seo--red.be-seo-bar__fill { background: linear-gradient(90deg, #EF4444, #DC2626); }

.be-seo-items { display: flex; flex-direction: column; gap: 10px; }
.be-seo-item { display: flex; align-items: flex-start; gap: 10px; }
.be-seo-icon { flex-shrink: 0; margin-top: 1px; }
.be-seo-icon--ok { color: #059669; }
.be-seo-icon--fail { color: #DC2626; }
.be-seo-item__label { font-size: 13px; font-weight: 700; color: #e2e8f0; }
.be-seo-item__hint { font-size: 11px; color: rgba(255,255,255,0.45); }

.be-img-progress { margin-bottom: 10px; }
.be-img-progress__bar { height: 4px; background: rgba(255,255,255,0.12); border-radius: 4px; overflow: hidden; margin-bottom: 6px; }
.be-img-progress__fill { height: 100%; width: 60%; background: linear-gradient(90deg, $orange, #F59E0B); border-radius: 4px; animation: imgProgress 3s ease-in-out infinite; }
.be-img-progress__hint { font-size: 11px; color: rgba(255,255,255,0.45); }

@keyframes imgProgress {
  0% { width: 10%; }
  50% { width: 80%; }
  100% { width: 10%; }
}
</style>
